import React, { useState, useEffect, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import DataTableGrid from '../../../../components/common/DataTableGrid';
import { ReactComponent as EditIcon } from '../../../../images/note-pad.svg';
import { ReactComponent as TrashIcon } from '../../../../images/trash.svg';
import { ReactComponent as SaveIcon } from '../../../../images/save-01.svg';
import { ReactComponent as CancelIcon } from '../../../../images/x-close.svg';
import shutdownIcon from '../../../../images/shutdown.svg';
import { useAppDispatch } from '../../../../hooks/storeHooks';
import Loader from '../../../common/Loader/Loader';
import Modal from '../../../common/Modal/Modal';
import '../../WellCompletions.scss';

export interface ColumnConfig {
  key: string;
  header: string;
  type: 'input' | 'select';
  width?: number;
  options?: { key: any; value: string }[];
  isNumeric?: boolean;
}

export interface EditableTableRef {
  getCurrentData: () => any[];
  getCommittedData: () => any[];
  discardUnsaved: () => void;
}

interface EditableTableProps<T extends { Id: number; [key: string]: any }> {
  title: string;
  data?: T[];
  columns: ColumnConfig[];
  onFetchDefault?: () => any;
  defaultData?: any;
  defaultLoading?: boolean;
  createNewRow: (newId: number, defaultData?: any) => T;
  className?: string;
  validateRow?: (rowData: T, allTableData?: T[]) => string[] | Promise<string[]>;
  validateOnSaveOnly?: boolean;
}

const CompletionsEditableTable = forwardRef<EditableTableRef, EditableTableProps<any>>(
  (
    {
      title,
      data,
      columns,
      onFetchDefault,
      defaultData,
      defaultLoading,
      createNewRow,
      className,
      validateRow,
      validateOnSaveOnly = false,
    },
    ref,
  ) => {
    const dispatch = useAppDispatch();
    const [tableData, setTableData] = useState<any[]>([]);
    const [editingRows, setEditingRows] = useState<Set<number>>(new Set());
    const [editingValues, setEditingValues] = useState<Record<number, Partial<any>>>({});
    const [isLoadingDefault, setIsLoadingDefault] = useState(false);
    const [shouldCreateRowAfterFetch, setShouldCreateRowAfterFetch] = useState(false);
    const [validationErrors, setValidationErrors] = useState<Record<number, string[]>>({});
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<any>(null);

    useImperativeHandle(ref, () => ({
      getCurrentData: () => tableData,
      getCommittedData: () => tableData.filter((row) => !editingRows.has(row.Id)),
      discardUnsaved: () => {
        setTableData((prev) => prev.filter((row) => !editingRows.has(row.Id)));
        setEditingRows(new Set());
        setEditingValues({});
        setValidationErrors({});
      },
    }));

    const allowedNumericKeys = useMemo(
      () => ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', '.', '-', 'ArrowLeft', 'ArrowRight'],
      [],
    );

    const getNextId = useMemo(
      () => (data: any[]) => (data.length > 0 ? Math.max(...data.map((row) => row.Id)) + 1 : 1),
      [],
    );

    const fieldLabels = useMemo(() => {
      const labels: Record<string, string> = {};
      columns.forEach((col) => {
        labels[col.key] = col.header;
      });
      return labels;
    }, [columns]);

    const hasFieldError = useCallback(
      (rowId: number, fieldKey: string): boolean => {
        const rowErrors = validationErrors[rowId];
        if (!rowErrors || rowErrors.length === 0) return false;

        const fieldLabel = fieldLabels[fieldKey] || fieldKey;

        for (const error of rowErrors) {
          if (error.includes(fieldLabel)) {
            return true;
          }

          if (error.includes('Depth MD must be greater than Depth TVD')) {
            if (fieldKey === 'DepthMeasured' || fieldKey === 'DepthTrueVertical') {
              return true;
            }
          }

          if (error.includes('Mandrel #') && error.includes('Depth')) {
            if (fieldKey === 'DepthMeasured' || fieldKey === 'DepthTrueVertical') {
              return true;
            }
          }

          if (error.includes('Mandrel Number') && error.includes('already in use')) {
            if (fieldKey === 'MandrelNumber') {
              return true;
            }
          }
        }

        return false;
      },
      [validationErrors, fieldLabels],
    );

    const ControlledInput = React.memo(
      ({
        rowId,
        field,
        initialValue,
        onChange,
        isNumeric = false,
        hasError = false,
      }: {
        rowId: number;
        field: string;
        initialValue: any;
        onChange: (rowId: number, field: string, value: string) => void;
        isNumeric?: boolean;
        hasError?: boolean;
      }) => {
        const [value, setValue] = useState(initialValue?.toString() || '');
        const inputRef = useRef<HTMLInputElement>(null);

        useEffect(() => {
          setValue(initialValue?.toString() || '');
        }, [initialValue]);

        const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
          setValue(e.target.value);
        }, []);

        const handleBlur = useCallback(() => {
          onChange(rowId, field, value);
        }, [onChange, rowId, field, value]);

        const handleKeyDown = useCallback(
          (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (isNumeric && !/[0-9]/.test(e.key) && !allowedNumericKeys.includes(e.key)) {
              e.preventDefault();
            }

            if (e.key === 'Enter') {
              onChange(rowId, field, value);
              inputRef.current?.blur();
            }
          },
          [isNumeric, allowedNumericKeys, onChange, rowId, field, value],
        );

        return (
          <input
            ref={inputRef}
            type='text'
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={`completions-edit-table-row-field ${hasError ? 'error' : ''}`}
          />
        );
      },
    );

    useEffect(() => {
      setTableData(data || []);
    }, [data]);

    useEffect(() => {
      if (shouldCreateRowAfterFetch && defaultData && !isLoadingDefault) {
        const newId = getNextId(tableData);
        const newRow = createNewRow(newId, defaultData);

        setTableData((prev) => [newRow, ...prev]);
        setEditingRows((prev) => new Set([...prev, newRow.Id]));
        setEditingValues((prev) => ({ ...prev, [newRow.Id]: { ...newRow } }));
        setShouldCreateRowAfterFetch(false);
      }
    }, [shouldCreateRowAfterFetch, defaultData, isLoadingDefault, tableData, createNewRow, getNextId]);

    const handleAddNew = useCallback(async () => {
      if (isEditingAnyRow) {
        editingRows.forEach((rowId) => handleCancel(rowId));
      }
      if (onFetchDefault && !defaultData && !defaultLoading) {
        setIsLoadingDefault(true);
        try {
          await dispatch(onFetchDefault());
          setShouldCreateRowAfterFetch(true);
        } catch (error) {
          console.error('Error fetching default data:', error);
        } finally {
          setIsLoadingDefault(false);
        }
        return;
      }

      const newId = getNextId(tableData);
      const newRow = createNewRow(newId, defaultData);

      setTableData((prev) => [newRow, ...prev]);
      setEditingRows((prev) => new Set([...prev, newRow.Id]));
      setEditingValues((prev) => ({ ...prev, [newRow.Id]: { ...newRow } }));
    }, [tableData, onFetchDefault, defaultData, defaultLoading, dispatch, createNewRow, getNextId]);

    const handleEdit = useCallback((rowData: any) => {
      setEditingRows(new Set([rowData.Id]));
      setEditingValues({ [rowData.Id]: { ...rowData } });
      setValidationErrors((prev) => {
        const newErrors: Record<number, string[]> = {};
        if (prev[rowData.Id]) {
          newErrors[rowData.Id] = prev[rowData.Id];
        }
        return newErrors;
      });
    }, []);

    const handleSave = useCallback(
      async (rowId: number) => {
        const editedData = editingValues[rowId];
        if (!editedData) return;

        const parsedData = { ...editedData } as Record<string, any>;
        columns.forEach((col) => {
          if (col.isNumeric && parsedData[col.key] !== undefined) {
            const currentValue = parsedData[col.key];
            parsedData[col.key] = typeof currentValue === 'string' ? parseFloat(currentValue) || 0 : currentValue;
          }
        });

        if (validateRow) {
          try {
            const errors = await Promise.resolve(validateRow(parsedData, tableData));
            if (errors.length > 0) {
              setValidationErrors((prev) => ({ ...prev, [rowId]: errors }));
              return;
            } else {
              setValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[rowId];
                return newErrors;
              });
            }
          } catch (error) {
            console.error('Validation error:', error);
            setValidationErrors((prev) => ({ ...prev, [rowId]: ['Validation failed'] }));
            return;
          }
        }

        setTableData((prev) => prev.map((row) => (row.Id === rowId ? { ...row, ...parsedData } : row)));
        setEditingRows((prev) => {
          const newSet = new Set(prev);
          newSet.delete(rowId);
          return newSet;
        });
        setEditingValues((prev) => {
          const newValues = { ...prev };
          delete newValues[rowId];
          return newValues;
        });
      },
      [editingValues, columns, validateRow, tableData],
    );

    const handleCancel = useCallback((rowId: number) => {
      const isNewRow = !(data && data.some((row) => row.Id === rowId));
      if (isNewRow) {
        setTableData((prev) => prev.filter((row) => row.Id !== rowId));
      }
      setEditingRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(rowId);
        return newSet;
      });
      setEditingValues((prev) => {
        const newValues = { ...prev };
        delete newValues[rowId];
        return newValues;
      });
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[rowId];
        return newErrors;
      });
    }, []);

    const handleInputChange = useCallback(
      (rowId: number, field: string, value: string | number) => {
        setEditingValues((prev) => {
          const updatedValues = {
            ...prev,
            [rowId]: { ...prev[rowId], [field]: value },
          };

          if (validateRow && !validateOnSaveOnly) {
            const rowData = { ...updatedValues[rowId] };

            for (const col of columns) {
              if (col.isNumeric && rowData[col.key] !== undefined) {
                const currentValue = rowData[col.key];
                if (typeof currentValue === 'string') {
                  const numValue = parseFloat(currentValue);
                  rowData[col.key] = isNaN(numValue) ? 0 : numValue;
                }
              }
            }

            Promise.resolve(validateRow(rowData, tableData))
              .then((errors) => {
                setValidationErrors((prevErrors) => {
                  const hasErrors = errors.length > 0;
                  const hadErrors = prevErrors[rowId]?.length > 0;

                  if (!hasErrors && !hadErrors) {
                    return prevErrors;
                  }

                  if (hasErrors) {
                    const currentErrors = prevErrors[rowId] || [];
                    if (
                      currentErrors.length === errors.length &&
                      errors.every((error, idx) => error === currentErrors[idx])
                    ) {
                      return prevErrors;
                    }
                    return { ...prevErrors, [rowId]: errors };
                  } else {
                    const newErrors = { ...prevErrors };
                    delete newErrors[rowId];
                    return newErrors;
                  }
                });
              })
              .catch((error) => {
                console.error('Validation error during input change:', error);
                setValidationErrors((prevErrors) => ({
                  ...prevErrors,
                  [rowId]: ['Validation failed'],
                }));
              });
          }

          return updatedValues;
        });
      },
      [validateRow, columns, validateOnSaveOnly, tableData],
    );

    const handleDelete = useCallback((rowData: any) => {
      setItemToDelete(rowData);
      setShowDeleteModal(true);
    }, []);

    const confirmDelete = useCallback(() => {
      if (itemToDelete) {
        setTableData((prev) => prev.filter((row) => row.Id !== itemToDelete.Id));
        setItemToDelete(null);
      }
      setShowDeleteModal(false);
    }, [itemToDelete]);

    const ActionsCell = React.memo(({ row }: any) => {
      const isEditing = editingRows.has(row.original.Id);
      return (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
          {isEditing ? (
            <>
              <button
                type='button'
                onClick={() => handleCancel(row.original.Id)}
                className='completions-table-action-icons'
                title='Cancel'
              >
                <CancelIcon />
              </button>
              <button
                type='button'
                onClick={() => handleSave(row.original.Id)}
                className='completions-table-action-icons'
                title='Save'
              >
                <SaveIcon />
              </button>
            </>
          ) : (
            <>
              <button onClick={() => handleEdit(row.original)} className='completions-table-action-icons' title='Edit'>
                <EditIcon />
              </button>
              <button
                onClick={() => handleDelete(row.original)}
                className='completions-table-action-icons'
                title='Delete'
              >
                <TrashIcon />
              </button>
            </>
          )}
        </div>
      );
    });

    const DataCell = React.memo(
      ({ row, col }: any) => {
        const rowId = row.original.Id;
        const isEditing = editingRows.has(rowId);

        const currentValue = useMemo(() => {
          return isEditing ? (editingValues[rowId]?.[col.key] ?? row.original[col.key]) : row.original[col.key];
        }, [isEditing, editingValues, rowId, col.key, row.original]);

        const fieldHasError = useMemo(() => {
          return isEditing ? hasFieldError(rowId, col.key) : false;
        }, [isEditing, rowId, col.key, hasFieldError]);

        if (isEditing) {
          if (col.type === 'select' && col.options) {
            return (
              <select
                value={currentValue || ''}
                onChange={(e) =>
                  handleInputChange(
                    row.original.Id,
                    col.key,
                    col.isNumeric ? parseFloat(e.target.value) : e.target.value,
                  )
                }
                className={`completions-edit-table-row-field ${fieldHasError ? 'error' : ''}`}
              >
                {col.options.map((option: { key: any; value: string }) => (
                  <option key={option.key} value={option.key}>
                    {option.value}
                  </option>
                ))}
              </select>
            );
          }

          return (
            <ControlledInput
              rowId={row.original.Id}
              field={col.key}
              initialValue={currentValue}
              onChange={handleInputChange}
              isNumeric={col.isNumeric}
              hasError={fieldHasError}
            />
          );
        }

        if (col.type === 'select' && col.options) {
          const selectedOption = col.options.find(
            (opt: { key: any; value: string }) => String(opt.key) === String(currentValue),
          );
          return <span>{selectedOption?.value || currentValue}</span>;
        }

        return <span>{currentValue}</span>;
      },
      (prevProps, nextProps) => {
        const prevRow = prevProps.row.original;
        const nextRow = nextProps.row.original;
        const prevCol = prevProps.col;
        const nextCol = nextProps.col;

        return (
          prevRow.Id === nextRow.Id && prevRow[prevCol.key] === nextRow[prevCol.key] && prevCol.key === nextCol.key
        );
      },
    );

    const tableColumns = useMemo(
      () => [
        {
          Header: '',
          accessor: 'actions' as const,
          Cell: ActionsCell,
          width: 80,
          disableSortBy: true,
          disableFilters: true,
        },
        ...columns.map((col) => ({
          Header: col.header,
          accessor: col.key,
          Cell: (props: any) => <DataCell {...props} col={col} />,
          width: col.width,
          disableFilters: true,
        })),
      ],
      [columns, ActionsCell, DataCell],
    );

    const isEditingAnyRow = editingRows.size > 0;

    const LoadingState = React.memo(() => (
      <div className='installation-loading-state' style={{ padding: '40px', textAlign: 'center' }}>
        <Loader />
      </div>
    ));

    const ValidationOverlay = React.memo(({ errors }: { errors: string[] }) => (
      <div className='validation-overlay'>
        {errors.map((error, index) => (
          <div key={index}>• {error}</div>
        ))}
      </div>
    ));
    return (
      <div
        className='main-container'
        style={{
          margin: 'unset',
          width: '100%',
          maxWidth: '100%',
          height: '100%',
          maxHeight: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header className='cust-table-header'>
          <div
            className='cust-table-header-wrap'
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <h1 className='table-header-title'>{title}</h1>
            <button className='btn-prime' onClick={handleAddNew} disabled={isLoadingDefault || isEditingAnyRow}>
              <span className='ico-add' />
              <span className='text-button'>{isLoadingDefault ? 'Loading...' : 'Add New'}</span>
            </button>
          </div>
        </header>

        <div
          className={className || 'completions-bore-tab-table'}
          style={{
            width: '100%',
            maxWidth: '100%',
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
            position: 'relative',
          }}
        >
          {isLoadingDefault ? (
            <LoadingState />
          ) : (
            <>
              <DataTableGrid data={tableData} columns={tableColumns} maxHeight='100%' />
              {Object.entries(validationErrors).map(([rowIdStr, errors]) => {
                const rowId = parseInt(rowIdStr, 10);
                const rowIndex = tableData.findIndex((row) => row.Id === rowId);
                if (rowIndex === -1) return null;

                return (
                  <div
                    key={rowId}
                    style={{
                      position: 'absolute',
                      top: `${72 + rowIndex * 48}px`,
                      left: '0',
                      right: '0',
                      zIndex: 1000,
                    }}
                  >
                    <ValidationOverlay errors={errors} />
                  </div>
                );
              })}
            </>
          )}
        </div>

        <Modal
          open={showDeleteModal}
          setOpen={setShowDeleteModal}
          title='Confirm Delete'
          message={`Are you sure you want to delete this ${title.toLowerCase().includes('valve') ? 'valve' : title.toLowerCase().includes('bore') ? 'bore' : 'item'}?`}
          buttonText='Delete'
          buttonColor='bg-red-500'
          callbackOnConfirm={confirmDelete}
          Icon={shutdownIcon}
        />
      </div>
    );
  },
);

export default CompletionsEditableTable;
